import React from 'react'

// components
import { ButtonEdit } from '@/components/commons/buttons/ButtonEdit.component'
import { SocialIconLink } from '@/components/commons/links/SocialIconLink.component'
import { AnimateContainer } from '@/components/commons/container'

// styles
import { section, socialLinkContainer } from 'styles/v2/pages/profile'
import { ISocialPlatform } from '@/interfaces/socials'
import { useProfilePage } from '@/shared/hooks/profilePage'
import { SocialItemConfigure } from './SocialItemConfigure.component'
import {
  OnSaveParams,
  SocialConfiguratorModal,
} from '@/components/social-networks/SocialConfiguratorModal'
import { UserContext } from 'context'

// get array from enum ISocialPlatform
const ALL_SOCIALS = Object.keys(ISocialPlatform).map(
  (key) => ISocialPlatform[key]
)

export const SocialLinks: React.FC = () => {
  const { updateUser, user } = React.useContext(UserContext)
  const { profile, ownProfile } = useProfilePage()
  const [isEdditing, setIsEditing] = React.useState(false)

  const [isConfiguringSocial, setIsConfiguringSocial] = React.useState(false)
  const [selectedSocial, setSelectedSocial] =
    React.useState<ISocialPlatform>(null)
  const [showConfigurator, setShowConfigurator] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const configureSocial = (social: ISocialPlatform) => {
    setSelectedSocial(social)
    setShowConfigurator(true)
  }

  const onCancel = () => {
    setSelectedSocial(null)
    setIsConfiguringSocial(false)
    setShowConfigurator(false)
  }

  const onSave = async ({ identifier, audience }: OnSaveParams) => {
    setLoading(true)
    const socials = (user?.socials || []).filter(
      (spcial) => spcial.platform !== selectedSocial
    )
    socials.push({
      platform: selectedSocial,
      identifier,
      audience,
      isConfigured: true,
    })
    await updateUser({ socials })
    setLoading(false)
    onCancel()
  }

  const onRemove = async () => {
    setLoading(true)
    const socials = (user?.socials || []).filter(
      (spcial) => spcial.platform !== selectedSocial
    )
    await updateUser({ socials })
    setLoading(false)
    onCancel()
  }

  return (
    <>
      <SocialConfiguratorModal
        platform={selectedSocial}
        show={showConfigurator}
        close={onCancel}
        onSave={onSave}
        onRemove={onRemove}
        loading={loading}
      />
      <div className={section}>
        <div className="flex justify-space-between items-center mt-30 mb-12">
          <h3 className="text-16 text-primary m-0 mb-12">Socials</h3>
          {ownProfile && !isConfiguringSocial && (
            <ButtonEdit onClick={() => setIsEditing((e) => !e)} />
          )}
        </div>
        {!isConfiguringSocial || !ownProfile ? (
          <div className={socialLinkContainer}>
            {profile?.socials?.map((social, index) => (
              <div key={profile.username + '_' + social.platform}>
                <AnimateContainer
                  type="fadeInTop"
                  delay={index * 50}
                  setOpacityNone={true}
                  duration={0.2}
                  manualActive={true}
                  active={true}
                >
                  <SocialIconLink
                    social={social.platform}
                    username={social.identifier}
                    onClick={
                      isEdditing
                        ? () => configureSocial(social.platform)
                        : undefined
                    }
                  />
                </AnimateContainer>
              </div>
            ))}
            {isEdditing &&
              ownProfile &&
              ALL_SOCIALS.map((social) => {
                const isSocialConfigured = profile?.socials?.some(
                  (configuredSocial) => configuredSocial.platform === social
                )
                if (!isSocialConfigured) {
                  return (
                    <div key={social}>
                      <AnimateContainer
                        type="fadeInTop"
                        delay={0}
                        setOpacityNone={true}
                        duration={0.2}
                        manualActive={true}
                        active={true}
                      >
                        <SocialIconLink
                          social={social}
                          username={''}
                          filterGray={true}
                          onClick={() => configureSocial(social)}
                        />
                      </AnimateContainer>
                    </div>
                  )
                }
                return null
              })}
          </div>
        ) : (
          <SocialItemConfigure
            selectedSocial={selectedSocial}
            onCloseForm={onCancel}
          />
        )}
      </div>
    </>
  )
}
